
import { ReactNode, useEffect, useRef, useState } from "react";

interface StepCardProps {
  stepNumber: number;
  title: string;
  description: string;
  icon: ReactNode;
  animationDirection: "left" | "right";
  delay?: number;
  isVisible?: boolean;
}

export default function StepCard({ 
  stepNumber, 
  title, 
  description, 
  icon, 
  animationDirection,
  delay = 0,
  isVisible = true
}: StepCardProps) {
  const [isCardVisible, setIsCardVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const slideDirection = animationDirection === "right" ? "animate-slide-in-right" : "animate-slide-in-left";
  
  useEffect(() => {
    // For the first card, use the parent's isVisible prop
    if (stepNumber === 1) {
      setIsCardVisible(isVisible);
      return;
    }

    // For cards 2 and 3, use individual intersection observers
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsCardVisible(true);
          }
        });
      },
      {
        threshold: 0.4, // Trigger when 40% of the card is visible
        rootMargin: "0px"
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [stepNumber, isVisible]);
  
  return (
    <div 
      ref={cardRef}
      className={`group flex flex-col items-center text-center p-3 bg-white dark:bg-card rounded-lg shadow-md hover:shadow-lg transition-all duration-600 hover:-translate-y-1 h-[180px] ${
        isCardVisible ? slideDirection : ""
      } opacity-0 scale-95`}
      style={{ 
        animationDelay: `${delay}ms`,
        animationFillMode: "forwards",
        animationDuration: "1.6s"
      }}
    >
      <div className="mb-2 relative">
        {icon}
      </div>
      <h3 className="text-base font-semibold mb-2 text-foreground">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {description}
      </p>
    </div>
  );
}
